import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Paper, Stack, Alert, CircularProgress, Divider, InputAdornment, IconButton, Link } from '@mui/material';
import { AccountCircleOutlined, EmailOutlined, LockOutlined, Visibility, VisibilityOff, BadgeOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { api } from '../../../lib/api';
import './Login.css'; // Reuse the aesthetic grid and hero classes from Native Login

const Register: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.02)',
      borderRadius: '12px',
      transition: 'all 0.2s ease-in-out',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.12)',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'var(--login-accent)',
        borderWidth: '2px',
        boxShadow: '0 0 16px rgba(122, 167, 255, 0.15)',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.5)',
      '&.Mui-focused': {
        color: 'var(--login-accent)',
      }
    },
    '& .MuiInputBase-input': {
      color: 'var(--login-text)',
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.username || !formData.email || !formData.password || !formData.firstName || !formData.lastName) {
      setError(t('auth.registerEmpty', 'Bitte fülle alle Felder aus.'));
      return;
    }
    if (formData.password.length < 8) {
      setError(t('auth.passwordTooShort', 'Das Passwort muss mindestens 8 Zeichen lang sein.'));
      return;
    }
    
    setError(null);
    setLoading(true);
    try {
      const response = await api.register(formData);
      if (response.success) {
        setSuccessMessage(response.message || t('auth.registerSuccess', 'Registrierung eingereicht! Dein Konto wartet auf Admin-Freigabe.'));
      } else {
        setError(response.message || t('auth.registerFailed', 'Registrierung fehlgeschlagen.'));
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err?.message || err || t('auth.registerFailed', 'Registrierung fehlgeschlagen. Bitte versuche es später noch einmal.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="kavithai-login-root">
      {/* We reuse the login grid for the identical split-screen look */}
      <Box className="kavithai-login-grid">
        <Box className="kavithai-login-hero">
          <Typography variant="overline" className="kavithai-login-overline">
            Kavithai.de
          </Typography>
          <Typography variant="h3" className="kavithai-gradient-text kavithai-login-title">
            {t('auth.registerHeadline', 'Werde Teil der Schriftsteller-Community')}
          </Typography>
          <Typography className="kavithai-login-subtitle">
            {t('auth.registerSubline', 'Registriere dich jetzt, um deine erste Veröffentlichung zu erstellen und mitzudiskutieren.')}
          </Typography>
        </Box>

        <Paper elevation={0} className="kavithai-login-card" sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          <Stack spacing={3}>
            {successMessage ? (
              <Box textAlign="center" py={4}>
                <BadgeOutlined sx={{ fontSize: 64, color: 'var(--login-accent)', mb: 2 }} />
                <Typography variant="h5" className="kavithai-login-card-title" gutterBottom>
                  {t('auth.registerSuccessTitle', 'Fast geschafft!')}
                </Typography>
                <Typography variant="body1" className="kavithai-login-card-subtitle" sx={{ mb: 4 }}>
                  {successMessage}
                </Typography>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  className="kavithai-login-secondary-btn"
                  onClick={() => navigate('/')}
                >
                  {t('nav.home', 'Zurück zur Startseite')}
                </Button>
              </Box>
            ) : (
              <>
                <Box>
                  <Typography variant="h5" className="kavithai-login-card-title" gutterBottom sx={{ fontSize: '1.75rem', mb: 1 }}>
                    {t('nav.register', 'Registrieren')}
                  </Typography>
                  <Typography variant="body1" className="kavithai-login-card-subtitle" sx={{ fontSize: '0.95rem' }}>
                    {t('auth.registerPrompt', 'Erstelle jetzt deinen kostenlosen Account.')}
                  </Typography>
                </Box>
                
                <Divider className="kavithai-login-divider" sx={{ opacity: 0.5, my: 1 }} />
                
                <form onSubmit={handleRegister} style={{ width: '100%' }}>
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        name="firstName"
                        label={t('auth.firstNameLabel', 'Vorname')}
                        variant="outlined"
                        fullWidth
                        value={formData.firstName}
                        onChange={handleChange}
                        disabled={loading}
                        sx={textFieldSx}
                      />
                      <TextField
                        name="lastName"
                        label={t('auth.lastNameLabel', 'Nachname')}
                        variant="outlined"
                        fullWidth
                        value={formData.lastName}
                        onChange={handleChange}
                        disabled={loading}
                        sx={textFieldSx}
                      />
                    </Stack>

                    <TextField
                      name="username"
                      label={t('auth.usernameLabel', 'Benutzername')}
                      variant="outlined"
                      fullWidth
                      value={formData.username}
                      onChange={handleChange}
                      disabled={loading}
                      sx={textFieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccountCircleOutlined sx={{ color: 'rgba(255, 255, 255, 0.4)' }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      name="email"
                      type="email"
                      label={t('auth.emailLabel', 'E-Mail Adresse')}
                      variant="outlined"
                      fullWidth
                      value={formData.email}
                      onChange={handleChange}
                      disabled={loading}
                      sx={textFieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailOutlined sx={{ color: 'rgba(255, 255, 255, 0.4)' }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      name="password"
                      label={t('auth.passwordLabel', 'Passwort')}
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      fullWidth
                      value={formData.password}
                      onChange={handleChange}
                      disabled={loading}
                      sx={textFieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlined sx={{ color: 'rgba(255, 255, 255, 0.4)' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              sx={{ color: 'rgba(255, 255, 255, 0.4)' }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />

                    {error && <Alert severity="error">{error}</Alert>}

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={loading}
                      className="kavithai-login-primary-btn"
                      sx={{ mt: 2, py: 1.5, borderRadius: '8px', fontSize: '1rem' }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : t('auth.registerButton', 'Account erstellen')}
                    </Button>
                    
                    <Box textAlign="center" pt={2} pb={1}>
                      <Typography variant="body2" color="rgba(255,255,255,0.6)">
                        {t('auth.alreadyHaveAccount', 'Du hast bereits einen Account?')} <Link component={RouterLink} to="/login" sx={{ color: 'var(--login-accent)', textDecoration: 'none', fontWeight: 500 }}>{t('nav.login', 'Anmelden')}</Link>
                      </Typography>
                    </Box>
                  </Stack>
                </form>
              </>
            )}
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default Register;
